import { compact, countBy, find, map, orderBy } from 'lodash';
import { useMemo } from 'react';

import { useCurrentSensorConfiguration } from '@/api/device';
import { OtaPlan } from '@/api/ota/types';
import { sensorDisplayName } from '@/utils';

/**
 * Hook to resolve BM node names and positions for changed versions, from before and after
 * OTA-style VersionStates. Omits nodes whose initial and target versions are the same.
 */
export const useBmNodeVersionUpdates = ({
    initialVersionState,
    targetVersionState,
}: Pick<OtaPlan, 'initialVersionState' | 'targetVersionState'>) => {
    const { data: sensorConfig } = useCurrentSensorConfiguration();

    return useMemo(() => {
        const appCounts = countBy(sensorConfig, 'app_name');
        const bmFirmwareUpdates = compact(
            map(targetVersionState.bmNodeVersions, (targetVersion, bmNodeId) => {
                if (initialVersionState.bmNodeVersions?.[bmNodeId] === targetVersion) {
                    return null;
                }
                const sensor = find(sensorConfig, { bristlemouth_node_id: bmNodeId });
                if (!sensor) {
                    return {
                        bmNodeId,
                        name: `Unknown sensor ${bmNodeId}`,
                        from: '',
                        to: targetVersion,
                        position: Infinity,
                    };
                }

                const { position } = sensor;
                const positionSuffix = appCounts[sensor.app_name] > 1 ? ` [${position}]` : '';
                const name = position === 0 ? 'Spotter Bristlemouth' : sensorDisplayName(sensor, true) + positionSuffix;
                return {
                    bmNodeId,
                    name,
                    from: initialVersionState.bmNodeVersions?.[bmNodeId] ?? '',
                    to: targetVersion,
                    position,
                };
            })
        );

        return orderBy(bmFirmwareUpdates, 'position', 'desc');
    }, [initialVersionState.bmNodeVersions, targetVersionState.bmNodeVersions, sensorConfig]);
};
